import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Clock3, Mail, Copy, Download, Gift } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashLayout from "@/components/xuma/DashLayout";
import { Button } from "@/components/ui/button";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

export default function AdminWaitlist() {
  const { user, loading } = useAuth();
  const [rows, setRows] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (user?.role === "admin") api.get("/admin/waitlist").then(({ data }) => { setRows(data); setLoadingData(false); }).catch(() => setLoadingData(false));
  }, [user]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-xuma-primary" /></div>;
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;

  const copyEmails = () => {
    const emails = rows.map((r) => r.email).join(", ");
    navigator.clipboard.writeText(emails).then(() => toast.success(`${rows.length} correos copiados`)).catch(() => toast.error("No se pudo copiar"));
  };
  const exportCsv = () => {
    const header = "nombre,correo,plan,fecha\n";
    const body = rows.map((r) => `"${(r.name || "").replace(/"/g, '""')}","${r.email}","${r.plan}","${r.created_at}"`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "xuma-lista-espera.csv";
    a.click();
  };

  return (
    <DashLayout title="Lista de espera">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl bg-white border border-xuma-border p-5">
          <Clock3 className="h-5 w-5 text-xuma-primary" />
          <p className="mt-3 font-heading font-extrabold text-2xl text-xuma-dark">{rows.length}</p>
          <p className="text-sm text-xuma-muted">En lista de espera</p>
        </div>
        <div className="rounded-2xl bg-[#FFF3EE] border border-[#F7C9B8] p-5 col-span-2 lg:col-span-2 flex items-center gap-3">
          <Gift className="h-6 w-6 text-xuma-primary shrink-0" />
          <p className="text-sm text-xuma-dark">Cada persona en esta lista recibirá el <b>Plan Fundador gratis por 1 año</b> al activarse Premium. Dales seguimiento por correo.</p>
        </div>
      </div>

      <div className="flex justify-end gap-2 mb-3">
        <Button data-testid="copy-emails-btn" onClick={copyEmails} disabled={!rows.length} variant="outline" className="rounded-full h-9 gap-2 border-xuma-border">
          <Copy className="h-4 w-4" /> Copiar correos
        </Button>
        <Button data-testid="export-csv-btn" onClick={exportCsv} disabled={!rows.length} className="rounded-full h-9 gap-2 bg-xuma-dark hover:bg-black text-white">
          <Download className="h-4 w-4" /> Exportar CSV
        </Button>
      </div>

      {loadingData ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-xuma-primary" /></div>
      ) : (
        <div className="rounded-2xl bg-white border border-xuma-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-xuma-border text-left text-xs uppercase tracking-wider text-xuma-muted">
                  {["#", "Nombre", "Correo", "Plan", "Fecha"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.waitlist_id} data-testid={`waitlist-row-${i}`} className="border-b border-xuma-border last:border-0 hover:bg-xuma-dash/40 transition-colors">
                    <td className="px-4 py-3 text-xuma-muted">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-xuma-dark">{r.name || "—"}</td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${r.email}`} className="text-xuma-primary inline-flex items-center gap-1.5 hover:underline">
                        <Mail className="h-3.5 w-3.5" /> {r.email}
                      </a>
                    </td>
                    <td className="px-4 py-3 capitalize">{(r.plan || "premium").replace("_", " ")}</td>
                    <td className="px-4 py-3 text-xuma-muted">{fmt(r.created_at)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-xuma-muted">Aún no hay nadie en la lista de espera.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashLayout>
  );
}
